import React from 'react';
import { I18n as I } from 'react-redux-i18n';

import {
  isNil,
  parseDate,
  AuctionStatuses,
  BidStatuses,
  ConfirmationModal,
  LoaderOverlay,
  PartyTypes,
} from 'vdlib';
import LookupsHelper from 'lib/LookupsHelper';
import { usePartyFlags } from 'lib/Hooks';

import { history } from 'data/Store';

import Portlet from 'components/layout/Portlet';
import AuctionHistoryGrid from 'containers/auctions/AuctionHistoryGridContainer';
import FolderDataPane from './FolderDataPane';
import PartDataPane from './PartDataPane';
import AuctionNotification from './AuctionNotification';
import AuctionBids from 'components/bids/AuctionBids';
import AuctionBidModal from 'components/bids/AuctionBidModal';
import AuctionDurationModal from './AuctionDurationModal';
import AuctionCommentsModal from './AuctionCommentsModal';

import AuctionGarageData from 'components/garages/AuctionGarageData';
import AuctionInsuranceData from 'components/parties/AuctionInsuranceData';
import AuctionMerchantData from 'components/parties/AuctionMerchantData';

import AuctionImages from 'containers/auctions/AuctionImagesContainer';

import getPortletActions, {
  PortletActionTypes,
} from './ui-data/AuctionPortletActions';

import { Button, Tooltip, Row, Col, Tabs } from 'antd';

const { TabPane } = Tabs;

const afterActiveStatuses = [
  AuctionStatuses.ForAcceptance,
  AuctionStatuses.ForDispatch,
  AuctionStatuses.Finished,
];

const modalTypes = {
  acceptAuction: 'accept-auction',
  auctionComments: 'auction-comments',
  auctionDuration: 'auction-duration',
  cancelAuction: 'cancel-auction',
  dispatchAuction: 'dispatch-auction',
  dispatchProblem: 'dispatch-problem',
  rejectAuction: 'reject-auction',
};

const isValidId = id => {
  try {
    return String(parseInt(id)) === String(id);
  } catch (e) {
    return false;
  }
};

const AuctionDetail = ({
  acceptAuction,
  addedAuctionComment,
  auction,
  auctionStatuses,
  bids,
  bidStatuses,
  cancelAuction,
  cancelBid,
  children,
  clearCommentAuction,
  clearData,
  comments,
  commentAuction,
  createBid,
  dispatchAuction,
  endAuction,
  fetchAuction,
  fetchBids,
  fetchComments,
  fetchGarage,
  fetchInsurance,
  fetchImages,
  fetchingImages,
  fetchMerchant,
  fuelTypes,
  garageData,
  gearBoxes,
  images,
  imitationPartCategories,
  insuranceData,
  lang,
  loading,
  location,
  merchantData,
  params,
  partTypes,
  partyTypes,
  rejectAuction,
  startAuction,
  submitting,
  taxOffices,
  usedConditionTypes,
  user,
  vehicleTypes,
  wheelDrives,
  selectTempBid,
  selectingTempBid,
  selectedTempBid,
}) => {
  const [modalType, setModalType] = React.useState(null);
  //const [visible, setVisible] = React.useState(null);
  //const [activeIndex, setActiveIndex] = React.useState(0);
  const [modalBidPartType, setModalBidPartType] = React.useState(null);
  const [cancelBidId, setCancelBidId] = React.useState(null);

  const { isInsurance, isMerchant, isOperator, partyType } = usePartyFlags(
    user,
    partyTypes
  );

  // const status = React.useEffect(
  //   () =>
  //     auction
  //       ? LookupsHelper.getDescription(auctionStatuses, auction.StatusID)
  //       : null,
  //   [auction, auctionStatuses]
  // );

  const status = React.useMemo(
    () =>
      auction
        ? LookupsHelper.getDescription(auctionStatuses, auction.StatusID)
        : null,
    [auction, auctionStatuses]
  );

  const isViewOnly = React.useMemo(
    () =>
      isMerchant &&
      auction &&
      auction.MerchantID &&
      user &&
      user.PartyId !== auction.MerchantID,
    [user, auction, isMerchant]
  );

  const show = React.useMemo(
    () => ({
      garageData: !!garageData,
      insuranceData: !!insuranceData && !isInsurance,
      merchantData: !!merchantData && !isMerchant,
      auctionBidsProducts:
        (status === AuctionStatuses.Active && (isMerchant || isOperator)) ||
        (status === AuctionStatuses.ForSelection && !isMerchant),
      auctionBidsTable: false,
    }),
    [
      garageData,
      insuranceData,
      merchantData,
      isInsurance,
      isMerchant,
      status,
      isOperator,
    ]
  );
  const can = React.useMemo(() => {
    const obj = {
      startAuction: isOperator && status === AuctionStatuses.Pending,
      auctionComment:
        isOperator &&
        [
          AuctionStatuses.Active,
          AuctionStatuses.Pending,
          AuctionStatuses.ForSelection,
        ].includes(status),
      makeBid: isMerchant && status === AuctionStatuses.Active,

      cancelAuction:
        isInsurance &&
        [AuctionStatuses.Active, AuctionStatuses.ForSelection].includes(status),
      acceptAuction: isMerchant && status === AuctionStatuses.ForAcceptance,
    };
    obj.cancelBid = obj.makeBid && Array.isArray(bids);
    return obj;
  }, [status, bids, isOperator, isInsurance, isMerchant]);

  const auctionBidsProducsExtraProps = React.useMemo(
    () =>
      show.auctionBidsProducts && isInsurance
        ? {
            auctionStatus: status,
            auctionPartPrice: auction.PartPrice,
            partDescription: auction.PartDescription,
            imitationCategories: imitationPartCategories,
            usedConditionTypes: usedConditionTypes,
            onSelectBid: endAuction,
            onTempSelectBid: selectTempBid,
          }
        : {},
    [
      auction,
      show,
      isInsurance,
      imitationPartCategories,
      usedConditionTypes,
      endAuction,
      status,
    ]
  );

  const portletActions = React.useMemo(() => {
    const portletActionsCallbacks = {
      [PortletActionTypes.StartAuction]: () =>
        setModalType(modalTypes.auctionDuration),
      [PortletActionTypes.CommentAuction]: () =>
        setModalType(modalTypes.auctionComments),
      [PortletActionTypes.EditAuction]: () =>
        history.push({ pathname: location.pathname + '/edit' }),
      [PortletActionTypes.CreateInFolderAuction]: () =>
        history.push({ pathname: location.pathname + '/create-from-folder' }),
      [PortletActionTypes.AcceptAuction]: () =>
        setModalType(modalTypes.acceptAuction),
      [PortletActionTypes.RejectAuction]: () =>
        setModalType(modalTypes.rejectAuction),
      //[PortletActionTypes.RejectAuction]: () => props.rejectAuction(props.auction.ID),
      [PortletActionTypes.DispatchAuction]: () =>
        setModalType(modalTypes.dispatchAuction),
      [PortletActionTypes.DispatchAuctionProblem]: () =>
        setModalType(modalTypes.dispatchProblem),
      [PortletActionTypes.CancelAuction]: () =>
        setModalType(modalTypes.cancelAuction),
    };

    const portletActionsRaw = getPortletActions(
      partyType,
      status,
      portletActionsCallbacks,
      this
    );

    return Array.isArray(portletActionsRaw) && !isViewOnly
      ? portletActionsRaw.map((ra, idx) => (
          <Tooltip title={ra.title}>
            <Button
              type="primary"
              shape="circle"
              style={{
                borderColor: ra.className,
                backgroundColor: 'white',
                marginRight: 5,
                borderRadius: '50% !important',
                color: ra.className,
              }}
              icon={ra.icon}
              onClick={ra.onClick}
            />
          </Tooltip>
        ))
      : false;
  }, [setModalType, isViewOnly, partyType, status]); //eslint-disable-line

  const handleBidSubmit = React.useCallback(
    (bid, comments, usedConditionTypeID, imitationCategoryID) => {
      createBid(auction.ID, [
        {
          Amount: bid.amount,
          Comments: comments,
          PartTypeID: bid.partType.ID,
          ImitationCategoryID:
            bid.partType.Description === 'Imitation'
              ? imitationCategoryID
              : null,
          UsedConditionTypeID:
            bid.partType.Description === 'Used' ? usedConditionTypeID : null,
        },
      ]);
    },
    [createBid, auction]
  );

  const init = React.useCallback(() => {
    clearData();
    fetchAuction();
    fetchBids();
    fetchComments();
  }, []); //eslint-disable-line

  // init effect
  React.useEffect(() => {
    if (isValidId(params.id)) init();
  }, [params.id, submitting]); //eslint-disable-line

  React.useEffect(() => {
    if (!user || !auction) return;
    const { PartyId } = user;
    const { GarageID, InsuranceID, MerchantID } = auction;
    const merchantMatch = MerchantID === PartyId;
    if (auction.FolderID) fetchImages(auction.FolderID);

    if (afterActiveStatuses.indexOf(status) >= 0) {
      if (merchantMatch) {
        fetchInsurance(InsuranceID);
        fetchGarage(GarageID);
      } else if (InsuranceID === PartyId || isOperator) {
        fetchMerchant(MerchantID);
      }
    }

    if (merchantMatch && status === AuctionStatuses.ForAcceptance)
      fetchGarage(GarageID);
  }, [auction]); //eslint-disable-line

  // cleanup effect
  React.useEffect(() => {
    return () => {
      clearData();
    };
  }, []); //eslint-disable-line

  const hideModal = React.useCallback(() => setModalType(null), [setModalType]);

  React.useEffect(() => {
    if (!submitting) {
      hideModal();
      setModalBidPartType(null);
      setCancelBidId(null);
    }
  }, [submitting]); //eslint-disable-line

  React.useEffect(() => {
    if (!selectingTempBid && selectedTempBid) {
      fetchBids(params.id);
    }
  }, [selectingTempBid]); //eslint-disable-line

  // Reload comments after comment save
  React.useEffect(() => {
    if (addedAuctionComment) {
      clearCommentAuction();
      fetchComments();
    }
  }, [addedAuctionComment]); //eslint-disable-line

  if (!isValidId(params.id)) return false;

  let acceptedBid =
    show.merchantData && Array.isArray(bids) && bids.length > 0
      ? bids.find(
          b =>
            b.StatusID ===
            LookupsHelper.getIdByDescription(bidStatuses, BidStatuses.Won)
        )
      : {};

  const portletTitle = isMerchant
    ? `${I.t('AUCTION_C')} - ${auction?.ID}`
    : `${I.t('FOLDER_NO_C')} - ${auction?.FolderNumber}`;

  const showAuctionBidModal =
    !!bids &&
    status === AuctionStatuses.Active &&
    !!modalBidPartType &&
    isMerchant;

  const renderDetail = !!(location.pathname || '').match(/\/?auctions\/\d+$/);

  return renderDetail ? (
    <div className="auction-wrap">
      <LoaderOverlay loading={!auction} />
      {auction && (
        <div>
          {!isViewOnly && (
            <AuctionNotification
              key="auction-notification"
              endDate={parseDate(auction.EndDate)}
              hasBids={Array.isArray(bids) && bids.length > 0}
              isMerchant={isMerchant}
              status={status}
              partyTypeDescription={partyType}
              onAuctionFinish={init}
            />
          )}
          <Portlet
            key="main"
            icon="icon-settings"
            title={portletTitle}
            backButtonUrl={''}
            showBackButton={true}
            actions={portletActions}
          >
            <Tabs defaultActiveKey="1" size="small">
              <TabPane tab={I.t('BASIC_INFO')} key="1">
                <Row gutter={[8, 8]}>
                  <Col xs={24} xxl={12}>
                    <PartDataPane
                      comments={comments}
                      auction={auction}
                      className=""
                      partyType={LookupsHelper.getDescription(
                        partyTypes,
                        user.PartyType
                      )}
                      showInsurance={
                        partyType === PartyTypes.Admin_Insurance ||
                        partyType === PartyTypes.Operator ||
                        partyType === PartyTypes.Insurance
                      }
                    />
                  </Col>
                  {show.merchantData ? (
                    <Col xs={24} xxl={12}>
                      <AuctionMerchantData
                        data={merchantData}
                        taxOffices={taxOffices}
                        bidAmount={acceptedBid.Amount}
                        initialAmount={(auction || {}).PartPrice}
                      />
                    </Col>
                  ) : (
                    false
                  )}
                  {!!garageData ? (
                    <Col xs={12} xxl={6}>
                      <AuctionGarageData
                        data={garageData}
                        taxOffices={taxOffices}
                      />
                    </Col>
                  ) : (
                    false
                  )}
                  {!show.insuranceData ? (
                    <Col xs={12} xxl={6}>
                      <AuctionInsuranceData data={insuranceData} />
                    </Col>
                  ) : (
                    false
                  )}
                </Row>
                {
                  // show "product" style bids for merchant|operator on active auction
                  show.auctionBidsProducts ? (
                    <Col xs={24}>
                      <AuctionBids
                        partyTypes={partyTypes}
                        partTypes={partTypes}
                        bids={bids}
                        user={user}
                        onOffer={setModalBidPartType}
                        partyType={partyType}
                        onCancel={setCancelBidId}
                        {...auctionBidsProducsExtraProps}
                        submitting={submitting}
                      />
                    </Col>
                  ) : (
                    false
                  )
                }
              </TabPane>
              <TabPane
                tab={isMerchant ? I.t('VEHICLE_DATA') : I.t('FOLDER_DATA')}
                key="2"
              >
                <Row>
                  <Col xs={24} xl={14}>
                    <AuctionImages
                      auction={auction}
                      fetchingImages={fetchingImages}
                      images={images}
                      onImagesChanged={fetchImages}
                      className={
                        show.merchantData || show.insuranceData
                          ? 'col-sm-7'
                          : null
                      }
                    />
                  </Col>
                  <Col xs={24} xl={10}>
                    <FolderDataPane
                      auction={auction}
                      comments={comments}
                      vehicleType={LookupsHelper.getDescription(
                        vehicleTypes,
                        auction.VehicleTypeID
                      )}
                      fuelType={LookupsHelper.getDescription(
                        fuelTypes,
                        auction.FuelTypeID
                      )}
                      gearBox={LookupsHelper.getDescription(
                        gearBoxes,
                        auction.GearboxID
                      )}
                      wheelDrive={LookupsHelper.getDescription(
                        wheelDrives,
                        auction.WheeldriveID
                      )}
                      showInsurance={
                        partyType === PartyTypes.Admin_Insurance ||
                        partyType === PartyTypes.Operator ||
                        partyType === PartyTypes.Insurance
                      }
                      showOwnerInfo={
                        partyType === PartyTypes.Admin_Insurance ||
                        partyType === PartyTypes.Operator ||
                        partyType === PartyTypes.Insurance ||
                        status === AuctionStatuses.Finished
                      }
                      viewOnly={isViewOnly}
                      status={LookupsHelper.getDescription(
                        auctionStatuses,
                        auction.StatusID
                      )}
                      partyType={LookupsHelper.getDescription(
                        partyTypes,
                        user.PartyType
                      )}
                    />
                  </Col>
                </Row>
              </TabPane>
              {isMerchant ? null : (
                <TabPane tab={I.t('HISTORY')} key="3">
                  <AuctionHistoryGrid auctionId={(auction || {}).ID} />
                </TabPane>
              )}
            </Tabs>
            <div className="row" />
          </Portlet>
          {
            // datepicker modal for starting an auction
            can.startAuction ? (
              <AuctionDurationModal
                show={modalType === modalTypes.auctionDuration}
                submitButton={I.t('APPROVAL')}
                loading={submitting}
                onSubmit={(endDate, comments) =>
                  startAuction(
                    {
                      ID: auction.ID,
                      EndDate: endDate,
                    },
                    comments
                  )
                }
                onHide={hideModal}
              />
            ) : (
              false
            )
          }
          {
            // datepicker modal for starting an auction
            can.auctionComment ? (
              <AuctionCommentsModal
                lang={lang}
                show={modalType === modalTypes.auctionComments}
                loading={submitting}
                comments={isNil(comments) ? '' : comments.Comments}
                onSubmit={comments =>
                  commentAuction(auction.ID, auction.StatusID, comments)
                }
                onHide={hideModal}
              />
            ) : (
              false
            )
          }
          {can.makeBid ? (
            <AuctionBidModal
              show={showAuctionBidModal}
              onHide={() => setModalBidPartType(null)}
              loading={submitting}
              onSubmit={handleBidSubmit}
              //onSubmit={ (partType, amount, comments, other) => createBid(auction.ID, partType.ID, amount, comments) }
              bid={
                !modalBidPartType
                  ? null
                  : (bids || []).find(b => b.PartTypeID === modalBidPartType.ID)
              }
              partType={modalBidPartType}
              imitationCategories={imitationPartCategories}
              usedConditionTypes={usedConditionTypes}
            />
          ) : (
            false
          )}

          {can.cancelBid ? (
            <ConfirmationModal
              promptText={I.t('MODAL_QUESTION_OFFER_DELETE')}
              loading={submitting}
              withComments={false}
              onHide={() => setCancelBidId(null)}
              show={!!cancelBidId}
              rejectButton={false}
              onSubmit={() => cancelBid(cancelBidId)}
            />
          ) : (
            false
          )}

          {can.cancelAuction ? (
            <ConfirmationModal
              loading={submitting}
              onHide={hideModal}
              onSubmit={() => cancelAuction(auction.ID)}
              promptText={I.t('MODAL_QUESTION_AUCTION_CANCEL')}
              rejectButton={false}
              show={modalType === modalTypes.cancelAuction}
              withComments={false}
            />
          ) : (
            false
          )}
          {can.acceptAuction && (
            <ConfirmationModal
              loading={submitting}
              onHide={hideModal}
              onSubmit={() => acceptAuction(auction.ID)}
              promptText={I.t('OFFER_APPROVE_ASK')}
              rejectButton={false}
              show={modalType === modalTypes.acceptAuction}
              title={I.t('OFFER_APPROVE')}
              withComments
            />
          )}
          {can.acceptAuction && (
            <ConfirmationModal
              loading={submitting}
              onHide={hideModal}
              onSubmit={(_, comments) => rejectAuction(auction.ID, comments)}
              promptText={I.t('MODAL_QUESTION_OFFER_REJECT')}
              rejectButton={false}
              show={modalType === modalTypes.rejectAuction}
              title={I.t('OFFER_REJECT')}
              withComments
            />
          )}
          {[
            AuctionStatuses.ForDispatch,
            AuctionStatuses.DispatchProblem,
          ].includes(status) &&
            isMerchant && (
              <ConfirmationModal
                loading={submitting}
                onHide={hideModal}
                onSubmit={() => dispatchAuction(auction.ID, false, '')}
                promptText={I.t('MODAL_QUESTION_PARTS_SENT')}
                rejectButton={false}
                show={modalType === modalTypes.dispatchAuction}
                title={I.t('PART_SEND')}
                withComments
              />
            )}
          {[
            AuctionStatuses.ForDispatch,
            AuctionStatuses.DispatchProblem,
          ].includes(status) &&
            isMerchant && (
              <ConfirmationModal
                loading={submitting}
                onHide={hideModal}
                onSubmit={(data, comments) =>
                  dispatchAuction(auction.ID, true, comments)
                }
                promptText={I.t('MODAL_QUESTION_PARTS_SENT_PROBLEM')}
                rejectButton={false}
                show={modalType === modalTypes.dispatchProblem}
                title={I.t('PART_SEND_PROBLEM')}
                withComments
              />
            )}
        </div>
      )}
    </div>
  ) : (
    <>{children}</>
  );
};

export default AuctionDetail;
